import { api } from '@hit-app/convex';
import { hasAdminAreaAccess } from '@hit-app/convex/permissions';
import { useMutation, useQuery } from 'convex/react';
import { Suspense, useEffect, type ComponentType } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { appRoutes, isAppRouteActive } from '../lib/routes';
import { useUserOrganizations } from '../lib/useUserOrganizations';
import { EventReminderDialog } from './EventReminderDialog';
import {
  CalendarIcon,
  HomeIcon,
  NewsIcon,
  PlatformIcon,
  SettingsIcon,
  UserIcon,
  UsersIcon,
  type IconProps,
} from './icons';
import { OrganizationSwitcher } from './OrganizationSwitcher';
import { PageSpinner } from './ui/feedback';
import { WhatsNewDialog } from './WhatsNewDialog';

interface NavItem {
  path: string;
  label: string;
  icon: ComponentType<IconProps>;
}

/**
 * App shell for all `/app` routes: sidebar, mobile navigation and app-wide dialogs.
 * Stays mounted across navigation; pages render through the nested `<Outlet />`.
 */
export function Layout() {
  const { pathname } = useLocation();
  const membership = useQuery(api.members.checkMembership, {});
  const recordActivity = useMutation(api.members.recordActivity);
  const organizations = useUserOrganizations();
  const hasOrg = membership?.hasOrg ?? false;

  // Record member activity on mount and whenever the tab becomes visible again.
  useEffect(() => {
    if (!hasOrg) return;

    recordActivity().catch(() => {});

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recordActivity().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [hasOrg, recordActivity]);

  const isAdmin = membership?.hasOrg ? hasAdminAreaAccess(membership.member.permissions) : false;
  const isPlatformAdmin = membership?.isPlatformAdmin ?? false;

  const navItems: NavItem[] = [
    { path: appRoutes.dashboard, label: 'Dashboard', icon: HomeIcon },
    { path: appRoutes.events, label: 'Events', icon: CalendarIcon },
    { path: appRoutes.news, label: 'News', icon: NewsIcon },
    { path: appRoutes.members, label: 'Mitglieder', icon: UsersIcon },
    { path: appRoutes.profile, label: 'Profil', icon: UserIcon },
    ...(isAdmin ? [{ path: appRoutes.admin, label: 'Verwaltung', icon: SettingsIcon }] : []),
    ...(isPlatformAdmin ? [{ path: appRoutes.platformAdmin, label: 'Plattform', icon: PlatformIcon }] : []),
  ];

  return (
    <div className="app-layout">
      {/* Desktop Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <h1>HiT-App</h1>
          <span>Netzwerk Management</span>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const active = isAppRouteActive(pathname, item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-nav-item relative ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-organization-switcher">
          <OrganizationSwitcher variant="sidebar" organizations={organizations} />
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <Suspense
          fallback={
            <div className="page-container">
              <PageSpinner />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      {/* Changelog-Dialog, erscheint nur bei noch nicht gesehenen Releases */}
      <WhatsNewDialog />

      {/* Erinnerungs-Dialog für zugesagte Events, die innerhalb der nächsten 24h beginnen */}
      <EventReminderDialog enabled={hasOrg} />

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav">
        <div className="mobile-nav-inner">
          {navItems.slice(0, 5).map((item) => {
            const active = isAppRouteActive(pathname, item.path);
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-item ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="w-6 h-6" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
